import type { SelectßOption } from '@otter-ai/den-components';
import type { Plan } from '@otter-ai/recall-client';
import { useTranslations } from 'next-intl';
import { useSubscription } from '@/features/account-settings/shared/hooks/useSubscription';
import { Security2FAType } from '@/shared/types/auth';
import { isEnterprisePlan, isTeamPlan } from '@/shared/utils/planUtils';

export type UseAvailableTwoFactorAuthMethodsResults = {
  availableMethods: Security2FAType[];
  options: SelectßOption;
  isLoading: boolean;
};

/**
 * Returns the two-factor authentication methods that are available to the current user.
 *
 * SMS is only available for Team or Enterprise plans.
 */
export function useAvailableTwoFactorAuthMethods({
  isBeforeLogin = false,
}: {
  isBeforeLogin?: boolean;
}) {
  const t = useTranslations('auth.two_factor.auth_methods');

  const { subscription, isLoading } = useSubscription();

  // When isBeforeLogin is true, we bypass subscription checks because
  // subscription data is not yet available in the two-factor-setup page before user login
  if (isBeforeLogin) {
    const availableMethods = [
      Security2FAType.AUTHENTICATION_APP,
      Security2FAType.SMS,
    ];
    const options = availableMethods.map((method) => ({
      label: t.has(method)
        ? t(method, { isBeforeLogin: isBeforeLogin.toString() })
        : method,
      value: method,
    }));

    return {
      availableMethods,
      options,
      isLoading: false,
    };
  }

  if (isLoading) {
    return {
      availableMethods: [],
      options: [],
      isLoading,
    };
  }

  if (!subscription) {
    throw new TwoFactorAuthMissingSubscriptionPlanError();
  }

  const isTeamOrEnterprisePlan =
    isTeamPlan(subscription.plan as unknown as Plan) ||
    isEnterprisePlan(subscription.plan as unknown as Plan);
  const availableMethods = isTeamOrEnterprisePlan
    ? [Security2FAType.AUTHENTICATION_APP, Security2FAType.SMS]
    : [Security2FAType.AUTHENTICATION_APP];

  const options = availableMethods.map((method) => ({
    label: t.has(method) ? t(method) : method,
    value: method,
  }));

  return {
    availableMethods,
    options,
    isLoading,
  };
}

class TwoFactorAuthMissingSubscriptionPlanError extends Error {
  name = 'TwoFactorAuthMissingSubscriptionPlanError';
  message = 'No subscription plan was found for the current user';
}
